import { States } from '../../../../app.router';

/* @ngInject */
export default class ValidationModalController {
    constructor($scope, DialogService, IAMService, $http, ProjectManager, ToastService, RulesApiService, $state) {
        this.$scope = $scope;
        this.DialogService = DialogService;
        this.IAMService = IAMService;
        this.$http = $http;
        this.ProjectManager = ProjectManager;
        this.ToastService = ToastService;
        this.RulesApiService = RulesApiService;
        this.$state = $state;

        // Cache for contact group details
        this.contactGroupCache = new Map();

        // Validation state
        this.isValidating = false;
        this.validationComplete = false;
        this.validationError = null;
        this.validationProgress = 0;
        this.currentProjectIndex = 0;
        this.currentProjectName = '';
        this.totalProjects = 0;
        this.isCancelled = false;

        // Results
        this.validationResults = null;
        this.projectsWithIssues = [];
        this.allProjects = []; // All projects including those without issues
        this.totalProjectsValidated = 0;
        this.totalRulesWithIssues = 0;
        this.totalIssues = 0;

        // View mode toggle: 'project' (default) or 'issueType'
        this.viewMode = 'project';
        this.issuesByType = [];

        this.showCloseConfirmation = false;
    }

    async startValidation() {
        this.isValidating = true;
        this.validationError = null;
        this.validationProgress = 0;
        this.currentProjectIndex = 0;
        this.projectsWithIssues = [];
        this.allProjects = [];
        this.isCancelled = false;

        try {
            // First, get all projects in the organization
            const projects = await this.fetchAllProjects();
            this.totalProjects = projects.length;

            if (projects.length === 0) {
                this.validationError = 'No projects found in this organization.';
                this.isValidating = false;
                return;
            }

            // Validate each project with progress tracking
            const allResults = [];
            const validateProjectWithProgress = async (project, index) => {
                // Check if validation was cancelled
                if (this.isCancelled) {
                    return null;
                }

                this.currentProjectIndex = index;
                this.currentProjectName = project.displayName || project.name;
                this.validationProgress = ((index + 1) / projects.length) * 100;
                this.$scope.$applyAsync();

                try {
                    const result = await this.validateProject(project);
                    if (result && result.rules && result.rules.length > 0) {
                        return {
                            project,
                            validation: result
                        };
                    }
                    return null;
                } catch (error) {
                    console.error(`Failed to validate project ${project.name}:`, error); // eslint-disable-line no-console
                    // Continue with other projects instead of failing completely
                    return null;
                }
            };

            // Process projects sequentially to maintain progress tracking using recursive approach
            const processProjectsSequentially = async (index) => {
                if (index >= projects.length || this.isCancelled) {
                    return;
                }
                
                const result = await validateProjectWithProgress(projects[index], index);
                if (result) {
                    allResults.push(result);
                }
                
                // Process next project recursively
                await processProjectsSequentially(index + 1);
            };
            
            await processProjectsSequentially(0);
            
            if (this.isCancelled) {
                return;
            }

            // Process results
            await this.processValidationResults(allResults, projects);
            this.buildIssueTypeView();
            this.validationComplete = true;

        } catch (error) {
            console.error('Validation failed:', error); // eslint-disable-line no-console
            this.validationError = error.message || 'Validation failed. Please try again.';
        } finally {
            this.isValidating = false;
            this.$scope.$applyAsync();
        }
    }

    async fetchAllProjects() {
        const organizationName = this.ProjectManager.currentProject.organization;
        
        const fetchPages = async (pageToken = null, accumulatedProjects = []) => {
            const params = {
                organization: organizationName
            };
            if (pageToken) {
                params.pageToken = pageToken;
            }
            
            const response = await this.IAMService.projects(params);
            const allProjects = accumulatedProjects.concat(response.data);
            
            if (response.nextPageToken) {
                return fetchPages(response.nextPageToken, allProjects);
            }
            return allProjects;
        };
        
        return fetchPages();
    }

    async validateProject(project) {
        // Extract project ID from project name (format: projects/{PROJECT_ID})
        const projectId = project.name.split('/')[1];
        
        const response = await this.RulesApiService.validateProject(projectId);
        return response;
    }

    async processValidationResults(results, allProjects) {
        this.totalProjectsValidated = results.length;
        this.projectsWithIssues = [];
        this.allProjects = [];
        this.totalRulesWithIssues = 0;
        this.totalIssues = 0;

        // Create a map of projects with validation results for quick lookup
        const resultsMap = new Map();
        results.forEach(({ project, validation }) => {
            resultsMap.set(project.name, { project, validation });
        });

        // Process all projects (both with and without issues)
        allProjects.forEach((project) => {
            const result = resultsMap.get(project.name);
            
            if (result) {
                // Project has validation results (issues)
                const { validation } = result;
                if (validation.rules && validation.rules.length > 0) {
                    const projectEntry = {
                        projectId: project.name.split('/')[1],
                        displayName: project.displayName || project.name,
                        sensorCount: project.sensorCount || 0,
                        cloudConnectorCount: project.cloudConnectorCount || 0,
                        rules: validation.rules,
                        totalIssues: 0,
                        expanded: false,
                        hasIssues: true
                    };

                    // Filter out false positives and empty rules
                    projectEntry.rules = this.filterFalsePositiveIssues(validation.rules, project.sensorCount);

                    // Count total issues and mark rules needing additional details
                    projectEntry.rules.forEach((rule) => {
                        projectEntry.totalIssues += rule.issues.length;
                        this.totalRulesWithIssues += 1;
                        this.totalIssues += rule.issues.length;

                        rule.needsRuleDetails = rule.issues.some(issue =>
                            issue.type === 'NO_DEVICES_MATCHING_LABELS' ||
                            issue.type === 'LABEL_TAGS_WITHOUT_LABELS'
                        );

                        rule.needsContactGroupDetails = rule.issues.some(issue =>
                            issue.type === 'CONTACT_GROUPS_WITHOUT_CONTACTS'
                        );
                    });

                    projectEntry.hasIssues = projectEntry.totalIssues > 0;

                    this.allProjects.push(projectEntry);

                    if (projectEntry.totalIssues > 0) {
                        this.projectsWithIssues.push(projectEntry);
                    }
                } else {
                    // Project has no rules with issues, but still add to all projects list
                    this.allProjects.push({
                        projectId: project.name.split('/')[1],
                        displayName: project.displayName || project.name,
                        sensorCount: project.sensorCount || 0,
                        cloudConnectorCount: project.cloudConnectorCount || 0,
                        rules: [],
                        totalIssues: 0,
                        expanded: false,
                        hasIssues: false
                    });
                }
            } else {
                // Project was validated but has no issues at all
                this.allProjects.push({
                    projectId: project.name.split('/')[1],
                    displayName: project.displayName || project.name,
                    sensorCount: project.sensorCount || 0,
                    cloudConnectorCount: project.cloudConnectorCount || 0,
                    rules: [],
                    totalIssues: 0,
                    expanded: false,
                    hasIssues: false
                });
            }
        });

        // Sort all projects: projects with issues first (by issue count descending), then projects without issues (alphabetically)
        this.allProjects.sort((a, b) => {
            if (a.hasIssues && !b.hasIssues) return -1;
            if (!a.hasIssues && b.hasIssues) return 1;
            if (a.hasIssues && b.hasIssues) return b.totalIssues - a.totalIssues;
            return a.displayName.localeCompare(b.displayName);
        });

        // Sort projects with issues by number of issues (descending)
        this.projectsWithIssues.sort((a, b) => b.totalIssues - a.totalIssues);
    }

    async fetchRuleDetails(rule) {
        if (!rule.rule) {
            return;
        }

        // Prevent duplicate fetches
        if (rule.loadingDetails || rule.ruleDetails) {
            return;
        }

        rule.loadingDetails = true;

        try {
            const ruleName = rule.rule;
            const parts = ruleName.split('/');

            if (parts.length >= 4 && parts[0] === 'projects' && parts[2] === 'rules') {
                const ruleProjectId = parts[1];
                const ruleId = parts[3];
                
                const response = await this.RulesApiService.getProjectRule(ruleProjectId, ruleId);
                rule.ruleDetails = response;
            } else if (parts.length >= 4 && parts[0] === 'organizations' && parts[2] === 'rules') {
                const organizationId = parts[1];
                const ruleId = parts[3];
                
                const response = await this.RulesApiService.getOrganizationRule(organizationId, ruleId);
                rule.ruleDetails = response;
            }
        } catch (error) {
            console.error(`Failed to fetch rule details for ${rule.rule}:`, error); // eslint-disable-line no-console
        } finally {
            rule.loadingDetails = false;
        }
    }

    async fetchContactGroupDetails(organizationId, contactGroupIds) {
        if (!organizationId || !contactGroupIds || contactGroupIds.length === 0) {
            return {};
        }

        // Check cache first
        const uncachedIds = contactGroupIds.filter(id => !this.contactGroupCache.has(id));
        
        if (uncachedIds.length > 0) {
            try {
                // Fetch all contact groups for the organization
                const response = await this.IAMService.listContactGroups(organizationId);
                const contactGroups = response.contactGroups || [];
                
                // Cache all contact groups we received
                contactGroups.forEach(group => {
                    // Extract the ID from the group name (format: organizations/{orgId}/contactGroups/{id})
                    const parts = group.name.split('/');
                    const groupId = parts[parts.length - 1];
                    this.contactGroupCache.set(groupId, {
                        id: groupId,
                        displayName: group.displayName || groupId,
                        name: group.name
                    });
                });
            } catch (error) {
                console.error(`Failed to fetch contact groups for organization ${organizationId}:`, error); // eslint-disable-line no-console
                // Cache empty entries for failed IDs to prevent repeated failures
                uncachedIds.forEach(id => {
                    this.contactGroupCache.set(id, {
                        id: id,
                        displayName: id,
                        name: id
                    });
                });
            }
        }

        // Return the requested contact group details
        const result = {};
        contactGroupIds.forEach(id => {
            if (this.contactGroupCache.has(id)) {
                result[id] = this.contactGroupCache.get(id);
            } else {
                // Fallback if not found
                result[id] = {
                    id: id,
                    displayName: id,
                    name: id
                };
            }
        });

        return result;
    }

    async onProjectExpand(project) {
        if (project.detailsLoaded) {
            return; // Already loaded details for this project
        }

        project.loadingDetails = true;
        this.$scope.$applyAsync();

        try {
            // Process all rules in this project that need additional details
            const rulePromises = project.rules.map(async (rule) => {
                if (rule.needsRuleDetails && !rule.ruleDetails && !rule.loadingDetails) {
                    await this.fetchRuleDetails(rule);
                }

                if (rule.needsContactGroupDetails) {
                    // Check if any contact group issues don't have details yet
                    const contactGroupIssues = rule.issues.filter(issue => 
                        issue.type === 'CONTACT_GROUPS_WITHOUT_CONTACTS' && !issue.contactGroupDetails
                    );

                    if (contactGroupIssues.length > 0) {
                        // Extract organization ID
                        const organizationId = this.ProjectManager.currentProject.organization.split('/')[1];
                        
                        // Collect all contact group IDs from all contact group issues
                        const allContactGroupIds = new Set();
                        contactGroupIssues.forEach(issue => {
                            if (issue.contactGroupsWithoutContacts && issue.contactGroupsWithoutContacts.contactGroups) {
                                issue.contactGroupsWithoutContacts.contactGroups.forEach(groupName => {
                                    // Extract ID from group name (format: organizations/{orgId}/contactGroups/{id})
                                    const parts = groupName.split('/');
                                    const groupId = parts[parts.length - 1];
                                    allContactGroupIds.add(groupId);
                                });
                            }
                        });

                        if (allContactGroupIds.size > 0) {
                            const contactGroupDetails = await this.fetchContactGroupDetails(
                                organizationId, 
                                Array.from(allContactGroupIds)
                            );
                            
                            // Attach contact group details to each issue
                            contactGroupIssues.forEach(issue => {
                                issue.contactGroupDetails = contactGroupDetails;
                            });
                        }
                    }
                }
                return Promise.resolve();
            });

            await Promise.all(rulePromises);
            project.detailsLoaded = true;
        } catch (error) {
            console.error(`Failed to load details for project ${project.displayName}:`, error); // eslint-disable-line no-console
        } finally {
            project.loadingDetails = false;
            this.$scope.$applyAsync();
        }
    }

    getRuleDisplayName(rule) {
        if (!rule) {
            return '';
        }

        // If we've already resolved the display name for this rule entry, return it
        if (rule.displayName) {
            return rule.displayName;
        }

        // Prevent duplicate fetches
        if (rule.loadingDisplayName) {
            // Return a stable fallback while loading
            const partsLoading = (rule.rule || '').split('/');
            return partsLoading[partsLoading.length - 1] || '';
        }

        const ruleName = rule.rule || '';
        const parts = ruleName.split('/');

        // Fallback to rule ID if parsing fails
        const fallbackId = parts[parts.length - 1] || '';

        // Kick off async fetch to populate displayName on the rule entry
        rule.loadingDisplayName = true;

        try {
            if (parts.length >= 4 && parts[0] === 'projects' && parts[2] === 'rules') {
                const projectId = parts[1];
                const ruleId = parts[3];
                this.RulesApiService.getProjectRule(projectId, ruleId)
                    .then(response => {
                        rule.displayName = response.displayName || fallbackId;
                    })
                    .catch(() => {
                        rule.displayName = fallbackId;
                    })
                    .finally(() => {
                        rule.loadingDisplayName = false;
                        this.$scope.$applyAsync();
                    });
            } else if (parts.length >= 4 && parts[0] === 'organizations' && parts[2] === 'rules') {
                const organizationId = parts[1];
                const ruleId = parts[3];
                this.RulesApiService.getOrganizationRule(organizationId, ruleId)
                    .then(response => {
                        rule.displayName = response.displayName || fallbackId;
                    })
                    .catch(() => {
                        rule.displayName = fallbackId;
                    })
                    .finally(() => {
                        rule.loadingDisplayName = false;
                        this.$scope.$applyAsync();
                    });
            } else {
                // Unknown format; resolve immediately to fallback
                rule.displayName = fallbackId;
                rule.loadingDisplayName = false;
            }
        } catch (_) {
            // Any unexpected error: use fallback
            rule.displayName = fallbackId;
            rule.loadingDisplayName = false;
            this.$scope.$applyAsync();
        }

        // Return fallback while async resolves
        return fallbackId;
    }

    getIssueTypeLabel(type) {
        const typeLabels = {
            'CONTACT_GROUPS_WITHOUT_CONTACTS': 'Contact groups without contacts',
            'LABEL_TAGS_WITHOUT_LABELS': 'Label tags without labels',
            'SCHEDULE_WITHOUT_TIME_LOCATION': 'Schedule without time zone',
            'MISSING_DEVICES': 'Missing devices',
            'NO_DEVICES_MATCHING_LABELS': 'No devices match labels'
        };
        return typeLabels[type] || type.toLowerCase().replace(/_/g, ' ');
    }

    getIssueDescription(issue, rule) {
        switch (issue.type) {
            case 'CONTACT_GROUPS_WITHOUT_CONTACTS':
                if (issue.contactGroupsWithoutContacts && issue.contactGroupsWithoutContacts.contactGroups) {
                    const contactGroups = issue.contactGroupsWithoutContacts.contactGroups;
                    const count = contactGroups.length;
                    
                    // Show the contact group names if available
                    if (contactGroups.length > 0) {
                        // Use contact group details if available, otherwise fall back to IDs
                        const groupNames = contactGroups.map(groupName => {
                            const parts = groupName.split('/');
                            const groupId = parts[parts.length - 1];
                            
                            // Check if we have contact group details
                            if (issue.contactGroupDetails && issue.contactGroupDetails[groupId]) {
                                return issue.contactGroupDetails[groupId].displayName;
                            }
                            
                            // Fallback to ID
                            return groupId;
                        });
                        
                        if (groupNames.length <= 3) {
                            // Show all names if 3 or fewer
                            return `Contact group${count > 1 ? 's' : ''} "${groupNames.join('", "')}" ${count > 1 ? 'have' : 'has'} no contacts assigned`;
                        }
                        // Show first 2 and indicate how many more
                        const remaining = groupNames.length - 2;
                        return `Contact groups "${groupNames.slice(0, 2).join('", "')}" and ${remaining} other${remaining > 1 ? 's' : ''} have no contacts assigned`;
                    }
                    
                    return `${count} contact group${count > 1 ? 's' : ''} have no contacts assigned`;
                }
                return 'Contact groups exist but have no contacts assigned';
            
            case 'LABEL_TAGS_WITHOUT_LABELS':
                // If we have rule details with device labels, show them
                if (rule && rule.ruleDetails && rule.ruleDetails.deviceLabels) {
                    const labels = Object.entries(rule.ruleDetails.deviceLabels);
                    if (labels.length > 0) {
                        const labelStrings = labels.map(([key, value]) => 
                            value ? `${key}=${value}` : key
                        );
                        return `Rule uses label tags (${labelStrings.join(', ')}) but no matching labels exist on devices`;
                    }
                }
                return 'Rule uses label tags but no matching labels exist on devices';
            
            case 'SCHEDULE_WITHOUT_TIME_LOCATION':
                return 'Schedule is configured but no time zone is specified';
            
            case 'MISSING_DEVICES':
                if (issue.missingDevices && issue.missingDevices.devices) {
                    const count = issue.missingDevices.devices.length;
                    return `${count} device${count > 1 ? 's' : ''} referenced in rule no longer exist`;
                }
                return 'Referenced devices no longer exist';
            
            case 'NO_DEVICES_MATCHING_LABELS':
                // If we have rule details with device labels, show them
                if (rule && rule.ruleDetails && rule.ruleDetails.deviceLabels) {
                    const labels = Object.entries(rule.ruleDetails.deviceLabels);
                    if (labels.length > 0) {
                        const labelStrings = labels.map(([key, value]) => 
                            value ? `${key}=${value}` : key
                        );
                        return `No devices match labels: ${labelStrings.join(', ')}`;
                    }
                }
                return 'No devices match the specified label criteria';
            
            default:
                return 'Unknown issue type';
        }
    }

    openProjectInNewTab(project) {
        const projectId = project.projectId;
        if (projectId) {
            const projectUrl = this.$state.href(States.SENSORS, { projectId: projectId }, { absolute: true });
            window.open(projectUrl, '_blank');
        }
    }

    openProjectForIssueType(project, issueType) {
        const projectId = project.projectId;
        if (!projectId) return;

        let state;
        switch (issueType) {
            case 'SCHEDULE_WITHOUT_TIME_LOCATION':
                state = States.PROJECT_SETTINGS;
                break;
            case 'CONTACT_GROUPS_WITHOUT_CONTACTS':
                state = States.ORGANIZATION_ALERT_CONFIG;
                break;
            case 'NO_DEVICES_MATCHING_LABELS':
            case 'MISSING_DEVICES':
            case 'LABEL_TAGS_WITHOUT_LABELS':
            default:
                state = States.SENSORS;
                break;
        }

        const url = this.$state.href(state, { projectId: projectId }, { absolute: true });
        window.open(url, '_blank');
    }

    openRuleInNewTab(rule, projectId) {
        if (!rule.rule) {
            return;
        }

        const ruleName = rule.rule;
        const parts = ruleName.split('/');
        
        let ruleProjectId;
        let ruleId;
        
        if (parts.length >= 4 && parts[0] === 'projects' && parts[2] === 'rules') {
            // Project rule: projects/{projectId}/rules/{ruleId}
            ruleProjectId = parts[1];
            ruleId = parts[3];
        } else if (parts.length >= 4 && parts[0] === 'organizations' && parts[2] === 'rules') {
            // Organization rule: organizations/{orgId}/rules/{ruleId}
            // For organization rules, we still need a project context to view them
            ruleProjectId = projectId;
            ruleId = parts[3];
        } else {
            console.error('Unknown rule format:', ruleName); // eslint-disable-line no-console
            return;
        }

        if (ruleProjectId && ruleId) {
            const ruleUrl = this.$state.href(States.ALERTS_RULES_DETAIL, { 
                projectId: ruleProjectId, 
                ruleId: ruleId 
            }, { absolute: true });
            window.open(ruleUrl, '_blank');
        }
    }

    filterFalsePositiveIssues(rules, sensorCount) {
        rules.forEach(rule => {
            if (rule.issues) {
                rule.issues = rule.issues.filter(issue =>
                    !(issue.type === 'NO_DEVICES_MATCHING_LABELS' && (sensorCount || 0) === 0)
                );
            }
        });
        return rules.filter(r => r.issues && r.issues.length > 0);
    }

    buildIssueTypeView() {
        const typeMap = {};
        this.allProjects.forEach(project => {
            if (!project.hasIssues) return;
            project.rules.forEach(rule => {
                (rule.issues || []).forEach(issue => {
                    if (!typeMap[issue.type]) {
                        typeMap[issue.type] = {
                            type: issue.type,
                            label: this.getIssueTypeLabel(issue.type),
                            entries: [],
                            totalOccurrences: 0,
                            expanded: false,
                            detailsLoaded: false,
                            loadingDetails: false,
                            uniqueProjects: [],
                            uniqueRules: []
                        };
                    }
                    typeMap[issue.type].entries.push({ project, rule, issue });
                    typeMap[issue.type].totalOccurrences++;
                });
            });
        });

        // Build deduplicated project and rule lists per type group
        Object.values(typeMap).forEach(group => {
            const projectMap = new Map();
            const ruleMap = new Map();

            group.entries.forEach(({ project, rule }) => {
                if (!projectMap.has(project.projectId)) {
                    projectMap.set(project.projectId, project);
                }
                const ruleKey = rule.rule || '';
                if (ruleKey && !ruleMap.has(ruleKey)) {
                    ruleMap.set(ruleKey, { rule, project });
                }
            });

            group.uniqueProjects = Array.from(projectMap.values())
                .sort((a, b) => a.displayName.localeCompare(b.displayName));
            group.uniqueRules = Array.from(ruleMap.values())
                .sort((a, b) => {
                    const aName = a.rule.displayName || a.rule.rule || '';
                    const bName = b.rule.displayName || b.rule.rule || '';
                    return aName.localeCompare(bName);
                });

            // Free entries array now that derived data is built
            delete group.entries;
        });

        this.issuesByType = Object.values(typeMap).sort((a, b) => b.totalOccurrences - a.totalOccurrences);
    }

    setViewMode(mode) {
        this.viewMode = mode;
    }

    async revalidateProject(project) {
        project.revalidating = true;
        this.$scope.$applyAsync();

        try {
            const projectId = project.projectId;
            const response = await this.RulesApiService.validateProject(projectId);

            // Count issues being removed from this project for total recalculation
            const oldIssueCount = project.totalIssues;
            const oldRuleCount = project.hasIssues ? project.rules.length : 0;

            if (response && response.rules && response.rules.length > 0) {
                // Filter false positives and empty rules
                const filteredRules = this.filterFalsePositiveIssues(response.rules, project.sensorCount);
                let newTotalIssues = 0;

                filteredRules.forEach(rule => {
                    newTotalIssues += rule.issues.length;
                    rule.needsRuleDetails = rule.issues.some(issue =>
                        issue.type === 'NO_DEVICES_MATCHING_LABELS' ||
                        issue.type === 'LABEL_TAGS_WITHOUT_LABELS'
                    );
                    rule.needsContactGroupDetails = rule.issues.some(issue =>
                        issue.type === 'CONTACT_GROUPS_WITHOUT_CONTACTS'
                    );
                });

                // Update project in-place
                project.rules = filteredRules;
                project.totalIssues = newTotalIssues;
                project.hasIssues = newTotalIssues > 0;
                project.detailsLoaded = false;
                project.expanded = project.hasIssues ? project.expanded : false;
            } else {
                // No issues found
                project.rules = [];
                project.totalIssues = 0;
                project.hasIssues = false;
                project.expanded = false;
            }

            // Recalculate totals
            this.totalIssues = this.totalIssues - oldIssueCount + project.totalIssues;
            this.totalRulesWithIssues = this.totalRulesWithIssues - oldRuleCount + (project.hasIssues ? project.rules.length : 0);

            // Rebuild derived views
            this.projectsWithIssues = this.allProjects.filter(p => p.hasIssues).sort((a, b) => b.totalIssues - a.totalIssues);
            this.buildIssueTypeView();

        } catch (error) {
            console.error(`Failed to revalidate project ${project.displayName}:`, error); // eslint-disable-line no-console
            this.ToastService.showSimple(`Failed to revalidate ${project.displayName}`);
        } finally {
            project.revalidating = false;
            this.$scope.$applyAsync();
        }
    }

    requestClose() {
        if (this.isValidating) {
            this.isCancelled = true;
            this.isValidating = false;
            this.validationError = 'Validation was cancelled by user.';
            this.$scope.$applyAsync();
        } else if (this.validationComplete && this.totalIssues > 0) {
            this.showCloseConfirmation = true;
            this.$scope.$applyAsync();
        } else {
            this.closeDialog();
        }
    }

    confirmClose() {
        this.closeDialog();
    }

    cancelClose() {
        this.showCloseConfirmation = false;
        this.$scope.$applyAsync();
    }

    closeDialog() {
        this.DialogService.cancel();
    }
}

